import React, { useState } from 'react';
import {
  Box,
  Button,
  Card,
  CardContent,
  CardActions,
  Grid,
  Typography,
  Chip,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  MenuItem,
  Switch,
  FormControlLabel,
  Alert,
  Tooltip,
  LinearProgress,
} from '@mui/material';
import {
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Sync as SyncIcon,
  CheckCircle as CheckIcon,
  Error as ErrorIcon,
  Settings as SettingsIcon,
  PlayArrow as StartIcon,
  Stop as StopIcon,
} from '@mui/icons-material';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { sisConfigurationApi, sisUtils } from '../../../services/sisApi';
import { SISConfiguration } from '../../../types/sis';

const SISConfigurations: React.FC = () => {
  const [open, setOpen] = useState(false);
  const [editingConfig, setEditingConfig] = useState<SISConfiguration | null>(null);
  const [formData, setFormData] = useState<Partial<SISConfiguration>>({
    name: '',
    api_base_url: '',
    auth_type: 'api_key',
    username: '',
    password: '',
    api_key: '',
    is_active: true,
    sync_frequency: 'daily',
  });

  const queryClient = useQueryClient();

  // Fetch configurations
  const { data: configsResponse, isLoading, error } = useQuery({
    queryKey: ['sisConfigurations'],
    queryFn: () => sisConfigurationApi.getAll(),
  });

  // Create/Update configuration mutation
  const createConfigMutation = useMutation({
    mutationFn: (data: SISConfiguration) => sisConfigurationApi.create(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['sisConfigurations'] });
      handleCloseDialog();
    },
  });

  const updateConfigMutation = useMutation({
    mutationFn: ({ id, data }: { id: number; data: Partial<SISConfiguration> }) =>
      sisConfigurationApi.update(id, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['sisConfigurations'] });
      handleCloseDialog();
    },
  });

  // Delete configuration mutation
  const deleteConfigMutation = useMutation({
    mutationFn: (id: number) => sisConfigurationApi.delete(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['sisConfigurations'] });
    },
  });

  // Test connection mutation
  const testConnectionMutation = useMutation({
    mutationFn: (id: number) => sisConfigurationApi.testConnection(id),
  });

  // Full sync mutation
  const fullSyncMutation = useMutation({
    mutationFn: (id: number) => sisConfigurationApi.fullSync(id),
  });

  const configurations = configsResponse?.data?.results || [];

  const handleOpenDialog = (config?: SISConfiguration) => {
    if (config) {
      setEditingConfig(config);
      setFormData(config);
    } else {
      setEditingConfig(null);
      setFormData({
        name: '',
        api_base_url: '',
        auth_type: 'api_key',
        username: '',
        password: '',
        api_key: '',
        is_active: true,
        sync_frequency: 'daily',
      });
    }
    setOpen(true);
  };

  const handleCloseDialog = () => {
    setOpen(false);
    setEditingConfig(null);
  };

  const handleSubmit = () => {
    if (editingConfig) {
      updateConfigMutation.mutate({
        id: editingConfig.id!,
        data: formData,
      });
    } else {
      createConfigMutation.mutate(formData as SISConfiguration);
    }
  };

  const handleDelete = (id: number) => {
    if (window.confirm('Are you sure you want to delete this configuration?')) {
      deleteConfigMutation.mutate(id);
    }
  };

  const handleTestConnection = (id: number) => {
    testConnectionMutation.mutate(id);
  };

  const handleFullSync = (id: number) => {
    if (window.confirm('This will perform a full synchronization. Continue?')) {
      fullSyncMutation.mutate(id);
    }
  };

  const getStatusColor = (isActive: boolean, lastSync?: string) => {
    if (!isActive) return 'error';
    if (!lastSync) return 'warning';
    
    const lastSyncDate = new Date(lastSync);
    const daysSinceSync = (Date.now() - lastSyncDate.getTime()) / (1000 * 60 * 60 * 24);
    
    if (daysSinceSync > 7) return 'warning';
    return 'success';
  };

  const formatLastSync = (dateString?: string) => {
    if (!dateString) return 'Never';
    return new Date(dateString).toLocaleString();
  };

  return (
    <>
      {/* Header */}
      <Box sx={{ mb: 3, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <Typography variant="h5">SIS Configurations</Typography>
        <Button
          variant="contained"
          startIcon={<AddIcon />}
          onClick={() => handleOpenDialog()}
        >
          Add Configuration
        </Button>
      </Box>

      {/* Loading */}
      {isLoading && <LinearProgress sx={{ mb: 2 }} />}

      {/* Error */}
      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          Failed to load configurations: {(error as any)?.message || 'Unknown error'}
        </Alert>
      )}

      {/* Live Configuration Alert */}
      <Alert severity="info" sx={{ mb: 3 }}>
        <Typography variant="body2">
          <strong>Live Integration Active:</strong> Connected to your production SIS database 
          (102.223.7.205:5432) with real-time synchronization capabilities.
        </Typography>
      </Alert>

      {/* Configurations Grid */}
      <Grid container spacing={3}>
        {configurations.map((config: any) => (
          <Grid item xs={12} md={6} lg={4} key={config.id}>
            <Card sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
              <CardContent sx={{ flexGrow: 1 }}>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 2 }}>
                  <Typography variant="h6" component="h3" noWrap>
                    {config.name}
                  </Typography>
                  <Chip
                    icon={config.is_active ? <CheckIcon /> : <ErrorIcon />}
                    label={config.is_active ? 'Active' : 'Inactive'}
                    color={getStatusColor(config.is_active, config.last_sync)}
                    size="small"
                  />
                </Box>

                <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                  <strong>URL:</strong> {config.api_base_url}
                </Typography>

                <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                  <strong>Auth:</strong> {config.auth_type.replace('_', ' ').toUpperCase()}
                </Typography>

                <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                  <strong>Frequency:</strong> {config.sync_frequency.replace('_', ' ')}
                </Typography>

                <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                  <strong>Last Sync:</strong> {formatLastSync(config.last_sync)}
                </Typography>

                <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                  <Chip 
                    label={config.sync_frequency} 
                    variant="outlined" 
                    size="small" 
                  />
                  <Chip 
                    label={config.auth_type} 
                    variant="outlined" 
                    size="small" 
                  />
                </Box>
              </CardContent>

              <CardActions sx={{ justifyContent: 'space-between', p: 2 }}>
                <Box>
                  <Tooltip title="Test Connection">
                    <IconButton 
                      size="small"
                      onClick={() => handleTestConnection(config.id!)}
                      disabled={testConnectionMutation.isPending}
                    >
                      <SettingsIcon />
                    </IconButton>
                  </Tooltip>
                  <Tooltip title="Full Sync">
                    <IconButton 
                      size="small"
                      onClick={() => handleFullSync(config.id!)}
                      disabled={!config.is_active || fullSyncMutation.isPending}
                    >
                      <SyncIcon />
                    </IconButton>
                  </Tooltip>
                </Box>
                <Box>
                  <Tooltip title="Edit">
                    <IconButton 
                      size="small"
                      onClick={() => handleOpenDialog(config)}
                    >
                      <EditIcon />
                    </IconButton>
                  </Tooltip>
                  <Tooltip title="Delete">
                    <IconButton 
                      size="small"
                      color="error"
                      onClick={() => handleDelete(config.id!)}
                    >
                      <DeleteIcon />
                    </IconButton>
                  </Tooltip>
                </Box>
              </CardActions>
            </Card>
          </Grid>
        ))}
      </Grid>

      {/* Empty State */}
      {!isLoading && configurations.length === 0 && (
        <Box sx={{ textAlign: 'center', py: 8 }}>
          <Typography variant="h6" color="text.secondary" gutterBottom>
            No SIS configurations found
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
            Create your first SIS configuration to start synchronizing data
          </Typography>
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={() => handleOpenDialog()}
          >
            Add Configuration
          </Button>
        </Box>
      )}

      {/* Create/Edit Dialog */}
      <Dialog 
        open={open} 
        onClose={handleCloseDialog} 
        maxWidth="md" 
        fullWidth
      >
        <DialogTitle>
          {editingConfig ? 'Edit Configuration' : 'Create SIS Configuration'}
        </DialogTitle>
        <DialogContent>
          <Box sx={{ pt: 1 }}>
            <Grid container spacing={2}>
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label="Configuration Name"
                  value={formData.name || ''}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                />
              </Grid>
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label="API Base URL"
                  value={formData.api_base_url || ''}
                  onChange={(e) => setFormData({ ...formData, api_base_url: e.target.value })}
                  placeholder="http://your-sis-server.com/api"
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  select
                  label="Authentication Type"
                  value={formData.auth_type || 'api_key'}
                  onChange={(e) => setFormData({ ...formData, auth_type: e.target.value as any })}
                >
                  {sisUtils.getAuthTypes().map((type) => (
                    <MenuItem key={type.value} value={type.value}>
                      {type.label}
                    </MenuItem>
                  ))}
                </TextField>
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  select
                  label="Sync Frequency"
                  value={formData.sync_frequency || 'daily'}
                  onChange={(e) => setFormData({ ...formData, sync_frequency: e.target.value as any })}
                >
                  {sisUtils.getSyncFrequencies().map((freq) => (
                    <MenuItem key={freq.value} value={freq.value}>
                      {freq.label}
                    </MenuItem>
                  ))}
                </TextField>
              </Grid>

              {/* Conditional fields based on auth type */}
              {(formData.auth_type === 'basic') && (
                <>
                  <Grid item xs={12} sm={6}>
                    <TextField
                      fullWidth
                      label="Username"
                      value={formData.username || ''}
                      onChange={(e) => setFormData({ ...formData, username: e.target.value })}
                    />
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <TextField
                      fullWidth
                      type="password"
                      label="Password"
                      value={formData.password || ''}
                      onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                    />
                  </Grid>
                </>
              )}

              {(formData.auth_type === 'api_key' || formData.auth_type === 'token') && (
                <Grid item xs={12}>
                  <TextField
                    fullWidth
                    label="API Key / Token"
                    value={formData.api_key || ''}
                    onChange={(e) => setFormData({ ...formData, api_key: e.target.value })}
                  />
                </Grid>
              )}

              <Grid item xs={12}>
                <FormControlLabel
                  control={
                    <Switch
                      checked={formData.is_active || false}
                      onChange={(e) => setFormData({ ...formData, is_active: e.target.checked })}
                    />
                  }
                  label="Active Configuration"
                />
              </Grid>
            </Grid>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDialog}>Cancel</Button>
          <Button
            onClick={handleSubmit}
            variant="contained"
            disabled={
              !formData.name || 
              !formData.api_base_url ||
              createConfigMutation.isPending ||
              updateConfigMutation.isPending
            }
          >
            {editingConfig ? 'Update' : 'Create'}
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default SISConfigurations;